import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, X, Save, Trash2, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * /console/jobs , weekly job scheduler for the Heritage team.
 * Reuses the same UX pattern as the Management Portal calendar but lives in
 * the admin Management Console and persists to `console_jobs`.
 */

const TYPE_TONES = {
  general:  { bg: "#FBF6E9", fg: "#3C2A1E", border: "#E8DFCB" },
  dispatch: { bg: "#F1E0C8", fg: "#8A5A1B", border: "#D9B985" },
  growing:  { bg: "#E5EFE2", fg: "#2F4A2A", border: "#B7CDB1" },
  admin:    { bg: "#E5DFE8", fg: "#3E1F46", border: "#C0AEC6" },
  delivery: { bg: "#F5D9CF", fg: "#A33030", border: "#E2A293" },
};

const TYPES = ["general", "dispatch", "growing", "admin", "delivery"];
const STATUSES = ["scheduled", "in_progress", "complete", "cancelled"];

function toLocalInput(d) {
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ConsoleJobs() {
  const [refDate, setRefDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay());
    return d;
  });
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(refDate); d.setDate(refDate.getDate() + i); return d;
  }), [refDate]);

  const weekStart = weekDays[0].toISOString();
  const weekEnd = new Date(weekDays[6].getTime() + 86400000).toISOString();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/jobs", { params: { start: weekStart, end: weekEnd } });
      setJobs(r.data || []);
    } catch { toast.error("Couldn't load jobs."); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [weekStart, weekEnd]);

  const jobsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => { map[d.toDateString()] = []; });
    jobs.forEach((j) => {
      const d = new Date(j.starts_at).toDateString();
      if (map[d]) map[d].push(j);
    });
    return map;
  }, [jobs, weekDays]);

  const shiftWeek = (dir) => {
    const d = new Date(refDate); d.setDate(refDate.getDate() + dir * 7); setRefDate(d);
  };

  const blank = () => ({
    id: null, title: "", description: "", starts_at: toLocalInput(new Date()),
    ends_at: "", location: "", assignee: "", job_type: "general", status: "scheduled",
  });

  const saveJob = async (j) => {
    if (!j.title.trim()) { toast.error("Title required."); return; }
    const payload = {
      title: j.title.trim(),
      description: j.description || "",
      starts_at: new Date(j.starts_at).toISOString(),
      ends_at: j.ends_at ? new Date(j.ends_at).toISOString() : null,
      location: j.location || "",
      assignee: j.assignee || "",
      job_type: j.job_type || "general",
      status: j.status || "scheduled",
    };
    try {
      if (j.id) await api.patch(`/console/jobs/${j.id}`, payload);
      else await api.post("/console/jobs", payload);
      toast.success("Job saved.");
      setEditing(null);
      await load();
    } catch { toast.error("Save failed."); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try { await api.delete(`/console/jobs/${id}`); setEditing(null); await load(); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="console-jobs">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">Heritage Dahlias · Console</div>
          <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1">Jobs</h1>
          <p className="text-[#7A6A55] mt-2 max-w-2xl text-sm">
            Schedule dispatch, growing, admin and delivery work for the team. Click any day to add a job, or any job to edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => shiftWeek(-1)} data-testid="jobs-prev-week" className="w-9 h-9 grid place-items-center rounded border border-[#E8DFCB] hover:bg-[#FBF6E9]"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-3 h-9 grid place-items-center rounded border border-[#E8DFCB] text-xs tracking-[0.15em] uppercase bg-white text-[#3C2A1E]">
            {weekDays[0].toLocaleDateString("en-NZ", { day: "numeric", month: "short" })} , {weekDays[6].toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
          </div>
          <button type="button" onClick={() => shiftWeek(1)} data-testid="jobs-next-week" className="w-9 h-9 grid place-items-center rounded border border-[#E8DFCB] hover:bg-[#FBF6E9]"><ChevronRight className="w-4 h-4" /></button>
          <button type="button" onClick={() => setEditing(blank())} data-testid="jobs-new" className="ml-2 inline-flex items-center gap-1.5 h-9 px-4 rounded bg-[#3E1F46] hover:bg-[#2C1531] text-white text-[10px] tracking-[0.25em] uppercase font-semibold">
            <Plus className="w-4 h-4" /> New job
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-3">
          {weekDays.map((d) => {
            const key = d.toDateString();
            const list = jobsByDay[key] || [];
            const isToday = key === new Date().toDateString();
            return (
              <div key={key} data-testid={`jobs-day-${d.toISOString().slice(0,10)}`} className={"bg-white border-2 rounded-md p-3 min-h-[180px] " + (isToday ? "border-[#C49247]" : "border-[#E8DFCB]")}>
                <button type="button" className="w-full text-left mb-2"
                  onClick={() => {
                    const start = new Date(d); start.setHours(9, 0, 0, 0);
                    setEditing({ ...blank(), starts_at: toLocalInput(start) });
                  }}
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#9C8B73] font-semibold">
                    {d.toLocaleDateString("en-NZ", { weekday: "short" })}
                  </div>
                  <div className="font-serif text-2xl text-[#3C2A1E]">{d.getDate()}</div>
                </button>
                <div className="space-y-1.5">
                  {list.map((j) => {
                    const tone = TYPE_TONES[j.job_type] || TYPE_TONES.general;
                    const done = j.status === "complete";
                    return (
                      <button
                        key={j.id}
                        type="button"
                        onClick={() => setEditing({ ...j, starts_at: toLocalInput(new Date(j.starts_at)), ends_at: j.ends_at ? toLocalInput(new Date(j.ends_at)) : "" })}
                        data-testid={`jobs-row-${j.id}`}
                        className="w-full text-left p-2 rounded border hover:translate-y-[-1px] transition"
                        style={{ background: tone.bg, color: tone.fg, borderColor: tone.border, opacity: done ? 0.55 : 1 }}
                      >
                        <div className={"font-serif text-sm leading-tight " + (done ? "line-through" : "")}>{j.title}</div>
                        <div className="text-[10px] opacity-75">
                          {new Date(j.starts_at).toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" })}
                          {j.assignee ? ` · ${j.assignee}` : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <JobEditor j={editing} onChange={setEditing} onSave={() => saveJob(editing)} onDelete={() => editing.id && deleteJob(editing.id)} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function JobEditor({ j, onChange, onSave, onDelete, onClose }) {
  return (
    <div data-testid="jobs-editor" className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#F8F4EA] border-2 border-[#3E1F46] rounded-md max-w-lg w-full p-6 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-[#3E1F46]" />
            <h3 className="font-serif text-2xl text-[#3C2A1E]">{j.id ? "Edit job" : "New job"}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        <Field label="Title *">
          <input value={j.title} onChange={(e) => onChange({ ...j, title: e.target.value })} data-testid="jobs-edit-title" className="ji" placeholder="e.g. Dispatch Mystery Box orders" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts *"><input type="datetime-local" value={j.starts_at} onChange={(e) => onChange({ ...j, starts_at: e.target.value })} data-testid="jobs-edit-starts" className="ji" /></Field>
          <Field label="Ends"><input type="datetime-local" value={j.ends_at || ""} onChange={(e) => onChange({ ...j, ends_at: e.target.value })} className="ji" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Assignee"><input value={j.assignee} onChange={(e) => onChange({ ...j, assignee: e.target.value })} data-testid="jobs-edit-assignee" className="ji" placeholder="Jess" /></Field>
          <Field label="Type">
            <select value={j.job_type} onChange={(e) => onChange({ ...j, job_type: e.target.value })} data-testid="jobs-edit-type" className="ji">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Location"><input value={j.location} onChange={(e) => onChange({ ...j, location: e.target.value })} className="ji" placeholder="The farm / customer address" /></Field>
        <Field label="Notes"><textarea value={j.description} onChange={(e) => onChange({ ...j, description: e.target.value })} rows={3} className="ji" /></Field>
        <Field label="Status">
          <select value={j.status} onChange={(e) => onChange({ ...j, status: e.target.value })} data-testid="jobs-edit-status" className="ji">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </Field>
        <div className="flex justify-between pt-2">
          {j.id ? (
            <button type="button" onClick={onDelete} data-testid="jobs-edit-delete" className="inline-flex items-center gap-1.5 h-10 px-4 rounded bg-white border border-[#A33030] text-[#A33030] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#FFF4F0]">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          ) : <span />}
          <button type="button" onClick={onSave} data-testid="jobs-edit-save" className="inline-flex items-center gap-1.5 h-10 px-5 rounded bg-[#3E1F46] hover:bg-[#2C1531] text-white text-xs tracking-[0.2em] uppercase font-semibold">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
        <style>{`.ji{width:100%;padding:8px 12px;border:1px solid #E8DFCB;border-radius:6px;background:white;font-size:14px;font-family:inherit;color:#3C2A1E;}.ji:focus{outline:none;border-color:#3E1F46;}textarea.ji{min-height:80px;}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase text-[#9C8B73] font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}
